import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@noor/api';
import { toast } from '@noor/lib';
import {
  ordersApi,
  ordersKeys,
  OrderStage,
  type OrderDetail,
  type OrdersListResult,
} from '@/entities/order';

/**
 * Order write-action mutations (cancel/finish/reorder/return/debt/payment/
 * courier-payment-select). All actions invalidate the order list + the affected
 * detail through `ordersKeys` so the UI refreshes (cpanel relied on a manual
 * `refetch` callback; v2 uses query invalidation). Toasts mirror cpanel copy —
 * including the deliberately-reused reorder/debt strings. The global MutationCache
 * handler surfaces the backend error message; optimistic mutations suppress it and
 * show a context-specific translated string instead.
 *
 * Cancel and finish are OPTIMISTIC (CLAUDE.md mutation contract): their
 * resulting stage is unambiguous and mirrors the mock backend's transition
 * (cancel → CANCELLED, finish → DELIVERED_FINISH). The other actions have no
 * client-predictable outcome (re-order/re-offer depend on dispatch, return
 * walks a multi-stage flow), so they stay invalidate-only.
 */
function useInvalidateOrders() {
  const queryClient = useQueryClient();
  return (orderId?: string | number) => {
    void queryClient.invalidateQueries({ queryKey: ordersKeys.lists() });
    if (orderId != null) {
      void queryClient.invalidateQueries({ queryKey: ordersKeys.detail(orderId) });
    }
  };
}

/** Snapshot of the caches an optimistic stage patch touches, for rollback. */
interface StageSnapshot {
  detail: OrderDetail | undefined;
  lists: [readonly unknown[], OrdersListResult | undefined][];
}

/**
 * Optimistically move an order to `stage` in the cached detail and every
 * cached list page (`onMutate`: cancel → snapshot → patch). The caller rolls
 * back via `rollbackStage` in `onError` and invalidates in `onSettled`.
 */
async function applyStageOptimistically(
  queryClient: QueryClient,
  id: string | number,
  patch: { stage: number; reason?: string },
): Promise<StageSnapshot> {
  await queryClient.cancelQueries({ queryKey: ordersKeys.all });
  const snapshot: StageSnapshot = {
    detail: queryClient.getQueryData<OrderDetail>(ordersKeys.detail(id)),
    lists: queryClient.getQueriesData<OrdersListResult>({
      queryKey: ordersKeys.lists(),
    }),
  };
  queryClient.setQueryData<OrderDetail>(ordersKeys.detail(id), (old) =>
    old ? { ...old, ...patch } : old,
  );
  queryClient.setQueriesData<OrdersListResult>({ queryKey: ordersKeys.lists() }, (old) =>
    old
      ? {
          ...old,
          data: old.data.map((row) =>
            String(row.id) === String(id) ? { ...row, ...patch } : row,
          ),
        }
      : old,
  );
  return snapshot;
}

/** Restore the caches captured by `applyStageOptimistically`. */
function rollbackStage(
  queryClient: QueryClient,
  id: string | number,
  snapshot: StageSnapshot | undefined,
): void {
  if (!snapshot) return;
  queryClient.setQueryData(ordersKeys.detail(id), snapshot.detail);
  for (const [queryKey, data] of snapshot.lists) {
    queryClient.setQueryData(queryKey, data);
  }
}

export interface CancelOrderArgs {
  id: string | number;
  reason: string;
  reorder?: boolean;
  cancelTransactions?: boolean;
}

export function useCancelOrder() {
  const { t } = useTranslation('orders');
  const queryClient = useQueryClient();
  const invalidate = useInvalidateOrders();
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: ({ id, reason, reorder, cancelTransactions }: CancelOrderArgs) =>
      ordersApi.cancel(id, {
        reason,
        reorder: reorder ?? false,
        cancel_transactions: cancelTransactions ?? false,
      }),
    onMutate: ({ id, reason }) =>
      applyStageOptimistically(queryClient, id, {
        stage: OrderStage.CANCELLED,
        reason,
      }),
    onSuccess: (data) => {
      // The cancel may partially fail server-side (re-order and/or
      // transaction-cancel are best-effort) — surface each outcome.
      if (data.cancel_transactions_error != null) {
        toast.warning(
          t('toast.cancelTxFailed', { error: data.cancel_transactions_error }),
        );
      }
      if (data.reorder_id != null) {
        toast.success(t('toast.reorderCreated', { id: data.reorder_id }));
      } else {
        toast.success(t('toast.cancelSuccess'));
      }
      if (data.reorder_error != null) {
        toast.warning(t('toast.reorderFailed', { error: data.reorder_error }));
      }
    },
    onError: (error, { id }, snapshot) => {
      rollbackStage(queryClient, id, snapshot);
      toast.error(getApiErrorMessage(error, t('toast.cancelError')));
    },
    onSettled: (_data, _error, { id }) => invalidate(id),
  });
}

export function useFinishOrder() {
  const { t } = useTranslation('orders');
  const queryClient = useQueryClient();
  const invalidate = useInvalidateOrders();
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: (id: string | number) => ordersApi.finish(id),
    onMutate: (id) =>
      applyStageOptimistically(queryClient, id, {
        stage: OrderStage.DELIVERED_FINISH,
      }),
    onSuccess: () => toast.success(t('toast.finishSuccess')),
    onError: (error, id, snapshot) => {
      rollbackStage(queryClient, id, snapshot);
      toast.error(getApiErrorMessage(error, t('toast.finishError')));
    },
    onSettled: (_data, _error, id) => invalidate(id),
  });
}

/**
 * Void the order's transactions. Invalidate-only: the backend decides which rows
 * flip to cancelled, so there is no client-predictable optimistic patch.
 */
export function useCancelOrderTransactions() {
  const { t } = useTranslation('orders');
  const invalidate = useInvalidateOrders();
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: (id: string | number) => ordersApi.cancelTransactions(id),
    onSuccess: () => toast.success(t('toast.cancelTxSuccess')),
    onError: (error) =>
      toast.error(t('toast.cancelTxFailed', { error: getApiErrorMessage(error, '') })),
    onSettled: (_data, _error, id) => invalidate(id),
  });
}

export function useReorderCourier() {
  const { t } = useTranslation('orders');
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: (id: string | number) => ordersApi.reorder(id),
    onSuccess: (_data, id) => {
      // cpanel reused the same toast for reorder + debt — preserved.
      toast.success(t('toast.reorderSuccess'));
      invalidate(id);
    },
  });
}

export interface ReOfferCourierArgs {
  removeOldCourier?: boolean;
  orderId: string | number;
  courierId: string | number;
}

/**
 * Re-offer the order to a SPECIFIC courier (distinct from blind re-order, which
 * lets the dispatch engine pick). Mirrors cpanel's `reOfferCourier` POST.
 */
export function useReOfferCourier() {
  const { t } = useTranslation('orders');
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: ({ orderId, courierId, removeOldCourier }: ReOfferCourierArgs) =>
      ordersApi.reOffer(orderId, courierId, removeOldCourier),
    onSuccess: (_data, { orderId }) => {
      toast.success(t('toast.reofferSuccess'));
      invalidate(orderId);
    },
  });
}

export function useReturnOrder() {
  const { t } = useTranslation('orders');
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: (id: string | number) => ordersApi.return(id),
    onSuccess: (_data, id) => {
      toast.success(t('toast.returnSuccess'));
      invalidate(id);
    },
  });
}

export function useDebtOrder() {
  const { t } = useTranslation('orders');
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: ({ id, needDebt }: { id: string | number; needDebt: boolean }) =>
      ordersApi.debt(id, { need_debt: needDebt }),
    onSuccess: (_data, { id }) => {
      // cpanel copy-paste: debt reuses the reorder toast strings.
      toast.success(t('toast.reorderSuccess'));
      invalidate(id);
    },
  });
}

export function useCourierPaymentSelect() {
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: (id: string | number) => ordersApi.courierPaymentSelect(id),
    onSuccess: (_data, id) => invalidate(id),
  });
}

export function usePaymentOrder() {
  const invalidate = useInvalidateOrders();
  return useMutation({
    mutationFn: ({ id, data }: { id: string | number; data: unknown }) =>
      ordersApi.payment(id, data),
    onSuccess: (_data, { id }) => invalidate(id),
  });
}
